import nursingHomeStore from "@source/store/nursingHomeStore";
import userStore from "@source/store/userStore";
import {ConsentStepsValues} from "@source/api/models/consentHelpers";
import {ConsentDocument, ConsentInputValues, ConsentTemplate, ConsentTemplateInput, ConsentTemplateInputType} from "@source/api/models/consentModels";

const escapePreviewHtml = (value: string) => value
  .replace(/&/g, "&amp;").replace(/</g, "&lt;").replace(/>/g, "&gt;")
  .replace(/"/g, "&quot;").replace(/'/g, "&#39;");

const DEFAULT_TIME_ZONE = "Australia/Sydney";

const previewDate = (value: unknown, timeZone?: string) => {
  if (!value) return "";
  const date = new Date(String(value));
  if (Number.isNaN(date.getTime())) return "";
  const format = (zone: string) =>
    new Intl.DateTimeFormat("en-GB", {day: "2-digit", month: "2-digit", year: "numeric", timeZone: zone}).format(date);
  try {
    return format(timeZone || DEFAULT_TIME_ZONE);
  } catch {
    return format(DEFAULT_TIME_ZONE);
  }
};

const settingsTimeZone = (person: unknown): string | undefined => {
  const timezone = (person as {settings?: {timezone?: unknown}} | undefined)?.settings?.timezone;
  return typeof timezone === "string" ? timezone : undefined;
};

const previewText = (value: unknown) =>
  typeof value === "string" || typeof value === "number" ? String(value) : "";

const previewName = (person?: {firstName?: string; lastName?: string}) =>
  [person?.firstName, person?.lastName].filter(Boolean).join(" ");

const ukConsentInputValue = (input: ConsentTemplateInput, value: unknown) => {
  if (typeof value !== "string" || !value.trim()) return "";
  const match = /^(\d{4})-(\d{2})-(\d{2})(?:T(\d{2}):(\d{2}))?$/.exec(value.trim());
  if (!match) return value.trim();
  const date = `${match[3]}/${match[2]}/${match[1]}`;
  if (input.type === ConsentTemplateInputType.DATETIME && match[4] && match[5]) {
    const hour = Number(match[4]);
    const hour12 = hour % 12 || 12;
    return `${date} ${hour12}:${match[5]} ${hour >= 12 ? "PM" : "AM"}`;
  }
  return date;
};

const previewInputValue = (input: ConsentTemplateInput, value: unknown) => {
  if (input.type === ConsentTemplateInputType.DATE || input.type === ConsentTemplateInputType.DATETIME) {
    return ukConsentInputValue(input, value);
  }
  const labels = new Map((input.options || []).map((option) => [option.id, option.label]));
  if (input.type === ConsentTemplateInputType.CHECKBOX) return value === true ? "Yes" : "";
  if (Array.isArray(value)) return value.map((item) => labels.get(String(item)) ?? String(item)).join(", ");
  if (typeof value === "string") return labels.get(value) ?? value;
  return "";
};

export const templatePreviewDocument = (template: ConsentTemplate, resident: any, values: ConsentStepsValues) => {
  const employee = userStore.getUserInfo();
  const home = nursingHomeStore.activeNursingHome;
  const familyId = values.familyFieldCompleterId || (values.familyMemberIds.length === 1 ? values.familyMemberIds[0] : "");
  const relation = (resident?.familyRelations || []).find((item: any) => item?.familyMember?._id === familyId);
  const family = relation?.familyMember;
  const doctor = resident?.attendedDoctor;
  const timeZone = settingsTimeZone(employee);
  const filled = new Map<string, string>([
    ["document.title", template.title],
    ["resident.firstName", previewText(resident?.firstName)],
    ["resident.lastName", previewText(resident?.lastName)],
    ["resident.name", previewName(resident)],
    ["resident.gender", previewText(resident?.gender)],
    ["resident.room", previewText(resident?.room)],
    ["resident.section", previewText(resident?.section)],
    ["resident.birthdate", previewDate(resident?.birthdate, timeZone)],
    ["resident.admissionDate", previewDate(resident?.admissionDate, timeZone)],
    ["resident.createdDate", previewDate(resident?.createdDate, timeZone)],
    ["resident.attendedDoctor.firstName", previewText(doctor?.firstName)],
    ["resident.attendedDoctor.lastName", previewText(doctor?.lastName)],
    ["resident.attendedDoctor.name", previewName(doctor)],
    ["employee.firstName", previewText(employee?.firstName)],
    ["employee.lastName", previewText(employee?.lastName)],
    ["employee.name", previewName(employee || undefined)],
    ["employee.email", previewText(employee?.email)],
    ["employee.phoneNumber", previewText(employee?.phoneNumber)],
    ["employee.role", previewText(employee?.account?.role)],
    ["employee.staffId", previewText(employee?.account?.stuffId)],
    ["employee.position", previewText(employee?.account?.position)],
    ["employee.employeeType", previewText(employee?.account?.employeeType)],
    ["nursingHome.name", previewText(home?.name)],
    ["nursingHome.address", previewText(home?.address)],
    ["nursingHome.phone", previewText(home?.phone)],
    ["nursingHome.email", previewText(home?.email)],
    ["family.firstName", previewText(family?.firstName)],
    ["family.lastName", previewText(family?.lastName)],
    ["family.name", previewName(family)],
    ["family.email", previewText(family?.email)],
    ["family.phoneNumber", previewText(family?.phoneNumber)],
    ["family.relationship", previewText(relation?.relationship)],
    ["family.contactType", previewText(relation?.contactType)],
  ]);
  template.employeeInputs.forEach((input) => {
    filled.set(`employee.${input.id}`, previewInputValue(input, values.employeeInputValues[input.id]));
  });
  const html = template.structure.html.replace(
    /{{\s*([a-zA-Z][a-zA-Z0-9_]*(?:\.[a-zA-Z][a-zA-Z0-9_-]*){1,2})\s*}}/g,
    (_placeholder, key: string) => escapePreviewHtml(filled.get(key) ?? ""),
  );
  return `<!doctype html><html><head><meta charset="utf-8"><style>${template.structure.css || ""}</style></head><body>${html}</body></html>`;
};

const asRecord = (value: unknown): Record<string, unknown> =>
  value && typeof value === "object" && !Array.isArray(value) ? value as Record<string, unknown> : {};

const entityId = (entity: unknown) => {
  if (!entity) return "";
  if (typeof entity === "string") return entity;
  return previewText(asRecord(entity)._id);
};

const booleanText = (value: unknown) => (value === true ? "Yes" : value === false ? "No" : "");

const recordName = (person: Record<string, unknown>) =>
  [previewText(person.firstName), previewText(person.lastName)].filter(Boolean).join(" ");

const presentationInputValue = (input: ConsentTemplateInput, value: unknown) => {
  if (input.type === ConsentTemplateInputType.DATE || input.type === ConsentTemplateInputType.DATETIME) {
    return ukConsentInputValue(input, value);
  }
  const labels = new Map((input.options || []).map((option) => [option.id, option.label]));
  if (input.type === ConsentTemplateInputType.CHECKBOX) {
    if (value === undefined) return "Yes / No";
    return value === true ? "Yes" : "No";
  }
  if (Array.isArray(value)) return value.map((item) => labels.get(String(item)) ?? String(item)).join(", ");
  return labels.get(String(value)) ?? String(value ?? "");
};

/** Resolved template preview for the family presentation, including answers that have not been saved yet. */
export const familyPresentationPreviewDocument = (document: ConsentDocument, familyInputValues: ConsentInputValues) => {
  const snapshot = document.templateSnapshot;
  if (!snapshot?.structure?.html) return "";
  const resident = asRecord(document.resident);
  const employee = asRecord(document.createdBy);
  const employeeAccount = asRecord(employee.account);
  const family = asRecord(document.categorySnapshot ? document.familyFieldCompleter : document.familyMember);
  const familyAccount = asRecord(family.account);
  const home = asRecord(document.nursingHome);
  const doctor = asRecord(resident.attendedDoctor);
  const familyMemberId = entityId(family);
  const relations = Array.isArray(resident.familyRelations) ? resident.familyRelations : [];
  const relation = relations.map(asRecord).find((item) => entityId(item.familyMember) === familyMemberId) || {};
  const timeZone = settingsTimeZone(employee);
  const filled = new Map<string, string>([
    ["document.title", document.title],
    ["document.createdAt", previewDate(document.createdAt, timeZone)],
    ["document.date", previewDate(new Date().toISOString(), timeZone)],
    ["document.version", String(snapshot.version ?? "")],
    ["resident.firstName", previewText(resident.firstName)],
    ["resident.lastName", previewText(resident.lastName)],
    ["resident.name", previewName(document.resident)],
    ["resident.gender", previewText(resident.gender)],
    ["resident.room", previewText(resident.room)],
    ["resident.section", previewText(resident.section)],
    ["resident.birthdate", previewDate(resident.birthdate, timeZone)],
    ["resident.admissionDate", previewDate(resident.admissionDate, timeZone)],
    ["resident.createdDate", previewDate(resident.createdDate, timeZone)],
    ["resident.attendedDoctor.firstName", previewText(doctor.firstName)],
    ["resident.attendedDoctor.lastName", previewText(doctor.lastName)],
    ["resident.attendedDoctor.name", recordName(doctor)],
    ["employee.firstName", previewText(employee.firstName)],
    ["employee.lastName", previewText(employee.lastName)],
    ["employee.name", recordName(employee)],
    ["employee.email", previewText(employee.email)],
    ["employee.phoneNumber", previewText(employee.phoneNumber)],
    ["employee.role", previewText(employeeAccount.role)],
    ["employee.staffId", previewText(employeeAccount.stuffId)],
    ["employee.position", previewText(employeeAccount.position)],
    ["employee.employeeType", previewText(employeeAccount.employeeType)],
    ["nursingHome.name", previewText(home.name)],
    ["nursingHome.address", previewText(home.address)],
    ["nursingHome.phone", previewText(home.phone)],
    ["nursingHome.email", previewText(home.email)],
    ["nursingHome.facilityCode", previewText(home.facilityCode)],
    ["family.firstName", previewText(family.firstName)],
    ["family.lastName", previewText(family.lastName)],
    ["family.name", recordName(family)],
    ["family.email", previewText(family.email)],
    ["family.phoneNumber", previewText(familyAccount.phoneNumber ?? family.phoneNumber)],
    ["family.address", previewText(familyAccount.address)],
    ["family.homeNumber", previewText(familyAccount.homeNumber)],
    ["family.enduringGuardianship", booleanText(familyAccount.enduringGuardianship)],
    ["family.powerOfAttorney", booleanText(familyAccount.powerOfAttorney)],
    ["family.relationship", previewText(relation.relationship)],
    ["family.contactType", previewText(relation.contactType)],
  ]);
  (snapshot.employeeInputs || []).forEach((input) => {
    filled.set(`employee.${input.id}`, presentationInputValue(input, document.employeeInputValues?.[input.id]));
  });
  (snapshot.familyMemberInputs || []).forEach((input) => {
    filled.set(`family.${input.id}`, presentationInputValue(input, familyInputValues[input.id]));
  });
  const html = snapshot.structure.html.replace(
    /{{\s*([a-zA-Z][a-zA-Z0-9_]*(?:\.[a-zA-Z][a-zA-Z0-9_-]*){1,2})\s*}}/g,
    (_placeholder, key: string) => escapePreviewHtml(filled.get(key) ?? ""),
  );
  return `<!doctype html><html><head><meta charset="utf-8"><style>${snapshot.structure.css || ""}</style></head><body>${html}</body></html>`;
};
